import React, { forwardRef, useState } from "react";
import { StyleSheet, TextInput as RNTextInput } from "react-native";
import { styleConstants as C } from "~/style/styleConstants";
import { Collapsible } from "./Collapsible";
import { Spacer } from "./Spacer";
import { Text } from "./Text";
import { View } from "./View";
import type { TextProps } from "./Text";
import type { ViewProps } from "./View";
import type { ReactNode } from "react";
import type {
  TextInputProps as RNTextInputProps,
  TextStyle,
} from "react-native";

const S = StyleSheet.create({
  textInput: {
    flex: 1,
    minHeight: 38,
    paddingHorizontal: C.spacingLarge,
  },
  spacer: { height: 8 },
  container: {
    flexDirection: "row",
    borderWidth: 1,
  },
});

export interface TextInputProps extends RNTextInputProps {
  sizeExtraSmall?: boolean;
  sizeSmall?: boolean;
  sizeMedium?: boolean;
  sizeLarge?: boolean;
  sizeExtraLarge?: boolean;

  colorTheme?: boolean;
  colorDark?: boolean;
  colorDarkSoft?: boolean;
  colorDarkSofter?: boolean;
  colorLight?: boolean;
  colorLightSoft?: boolean;
  colorLightSofter?: boolean;

  weightRegular?: boolean;
  weightSemiBold?: boolean;
  weightExtraBold?: boolean;

  forwardedRef?: React.Ref<RNTextInput>;

  label?: string;
  error?: boolean;
  caption?: string;

  leftComponent?: () => ReactNode;
  rightComponent?: () => ReactNode;

  labelProps?: TextProps;

  children?: ReactNode;
  containerProps?: ViewProps;
}

export const TextInput = forwardRef<RNTextInput, TextInputProps>(
  (
    {
      sizeExtraSmall,
      sizeSmall,
      sizeMedium,
      sizeLarge,
      sizeExtraLarge,

      colorTheme,
      colorDark,
      colorDarkSoft,
      colorDarkSofter,
      colorLight,
      colorLightSoft,
      colorLightSofter,

      weightRegular,
      weightSemiBold,
      weightExtraBold,

      style,

      label,
      error = false,
      caption,

      leftComponent,
      rightComponent,

      labelProps,
      containerProps,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const { onFocus, ...otherProps } = props;

    const editable = otherProps.editable ?? true;

    let fontSize: TextStyle["fontSize"] = C.fontSizeMedium;
    if (sizeExtraSmall) fontSize = C.fontSizeExtraSmall;
    else if (sizeSmall) fontSize = C.fontSizeSmall;
    else if (sizeMedium) fontSize = C.fontSizeMedium;
    else if (sizeLarge) fontSize = C.fontSizeLarge;
    else if (sizeExtraLarge) fontSize = C.fontSizeExtraLarge;

    let color: TextStyle["color"] = C.colorTextDark;
    if (!editable) color = C.colorTextDarkSofter;
    else if (colorTheme) color = C.colorTextTheme;
    else if (colorDark) color = C.colorTextDark;
    else if (colorDarkSoft) color = C.colorTextDarkSoft;
    else if (colorDarkSofter) color = C.colorTextDarkSofter;
    else if (colorLight) color = C.colorTextLight;
    else if (colorLightSoft) color = C.colorTextLightSoft;
    else if (colorLightSofter) color = C.colorTextLightSofter;

    let fontFamily: TextStyle["fontFamily"] = "Nunito-Regular";
    if (weightRegular) {
      fontFamily = "Nunito-Regular";
    } else if (weightSemiBold) {
      fontFamily = "Nunito-SemiBold";
    } else if (weightExtraBold) {
      fontFamily = "Nunito-ExtraBold";
    }

    let borderColor = C.colorBackgroundGray;
    if (error) {
      borderColor = C.colorBackgroundDanger;
    } else if (isFocused) {
      borderColor = C.colorBackgroundTheme;
    }

    const backgroundColor = editable
      ? C.colorBackgroundWhite
      : C.colorBackgroundLightDark;

    return (
      <View>
        {!!label && (
          <>
            <Text sizeMedium weightSemiBold {...labelProps}>
              {label}
            </Text>
            <View style={S.spacer} />
          </>
        )}
        <View
          centerContent
          borderRadiusSmall
          style={[S.container, { backgroundColor, borderColor }]}
          {...containerProps}
        >
          {leftComponent ? leftComponent() : null}
          <RNTextInput
            ref={ref}
            placeholderTextColor={C.colorTextDarkSofter}
            selectionColor={C.colorBackgroundTheme}
            onFocus={(e) => {
              onFocus?.(e);
              setIsFocused(true);
            }}
            onEndEditing={() => setIsFocused(false)}
            style={[
              S.textInput,
              {
                fontSize,
                color,
                fontFamily,
              },
              style,
            ]}
            {...otherProps}
          />
          {rightComponent ? rightComponent() : null}
        </View>
        <Collapsible collapsed={!caption}>
          <Spacer small />
          <Text sizeMedium colorDanger={error} weightRegular>
            {caption}
          </Text>
        </Collapsible>
      </View>
    );
  },
);
